import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useEffect } from 'react';

import useHandleError from '@/hooks/useHandleError';
import { axiosClient } from '@/services/BaseApi.service';

import type { UserAuthKeysType } from './UserAuthKeys.types';

export const useFetchAuthKeys = (userId?: number) => {
  const handleError = useHandleError();

  // If userId is provided, fetch for that user (admin view)
  // Otherwise fetch for current user (account page view)
  const endpoint = userId ? `auth_keys/users/${userId}` : 'auth_keys/';

  const { data, error, isError, refetch } = <
    {
      data: UserAuthKeysType[];
      error: AxiosError;
      isError: boolean;
      refetch: () => void;
    }
  >useQuery({
    queryKey: [endpoint]
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch user info.');
    }
  }, [isError]);

  return {
    data: data || [],
    refetch
  };
};

export const useMutationAuthKeys = (refetch: () => void, userId?: number) => {
  const handleError = useHandleError();
  const endpoint = userId ? `auth_keys/users/${userId}` : 'auth_keys/';

  return useMutation({
    mutationFn: () => {
      return axiosClient.post(endpoint);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to create auth key.');
    },
    onSuccess: () => {
      refetch();
    }
  });
};

export const useDeleteAuthKey = (refetch: () => void, userId?: number) => {
  const handleError = useHandleError();
  const baseEndpoint = userId ? `auth_keys/users/${userId}/` : 'auth_keys/';

  return useMutation({
    mutationFn: (keyId: number) => {
      return axiosClient.delete(`${baseEndpoint}${keyId}`);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to delete auth key.');
    },
    onSuccess: () => {
      refetch();
    }
  });
};

export const useUpdateAuthKey = (refetch: () => void, userId?: number) => {
  const handleError = useHandleError();
  const baseEndpoint = userId ? `auth_keys/users/${userId}/` : 'auth_keys/';

  return useMutation({
    mutationFn: (keyId: number) => {
      return axiosClient.put(`${baseEndpoint}${keyId}`);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update auth key.');
    },
    onSuccess: () => {
      refetch();
    }
  });
};

export const formatRoleLabel = (role: string) =>
  role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
